import React, { ReactNode, useState } from 'react';

import { CardHeader } from '../CardHeader';
import { Surface, Body, FullBleed, Footer, CardElevation, Section } from './styled';

const collapseAnimationVariants = {
  expanded: { height: 'auto' },
  collapsed: { height: 0 },
};

type CardAction = {
  title: string;
  onClick: () => void;
};

export type CardProps = {
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  subtitle?: string;
  headerActionSlot?: React.ReactNode;
  action?: CardAction;
  collapsable?: boolean;
  elevation?: CardElevation;
  accent?: 'info' | 'success' | 'error';
  children: ReactNode;
  onClose?: () => void;
  renderFooter?: () => ReactNode;
};

export const Card = ({
  className,
  style,
  title,
  subtitle,
  headerActionSlot,
  action,
  collapsable,
  elevation,
  accent,
  children,
  onClose,
  renderFooter,
}: CardProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };

  const headerMarkup = title && (
    <CardHeader
      title={title}
      subtitle={subtitle}
      actionSlot={headerActionSlot}
      action={action}
      onClickClose={onClose}
      isCollapsed={isCollapsed}
      onClickCollapse={collapsable ? toggleCollapsed : undefined}
    />
  );

  const showBody = (collapsable && !isCollapsed) || !collapsable;
  const bodyMarkup = (
    <Body
      animate={showBody ? 'expanded' : 'collapsed'}
      variants={collapseAnimationVariants}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      <Section>{children}</Section>
    </Body>
  );

  const footerMarkup = renderFooter !== undefined && <Footer>{renderFooter()}</Footer>;

  return (
    <Surface className={className} style={style} elevation={elevation} accent={accent}>
      {headerMarkup}
      {bodyMarkup}
      {footerMarkup}
    </Surface>
  );
};

Card.Surface = Surface;
Card.Section = Section;
Card.FullBleed = FullBleed;
Card.Footer = Footer;
